import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './ConseilOrientation.css';
import { 
  FaUserGraduate,
  FaChartLine,
  FaBriefcase,
  FaUniversity,
  FaLaptopCode,
  FaHeartbeat,
  FaArrowLeft,
  FaCheckCircle,
  FaCalendarAlt,
  FaRegClock,
  FaBook,
  FaGlobe,
  FaUsers,
  FaLightbulb,
  FaSearch,
  FaRegFileAlt,
  FaHandshake,
  FaStar,
  FaRocket,
  FaBuilding,
  FaGraduationCap
} from 'react-icons/fa';

const ConseilOrientation = () => {
  const navigate = useNavigate();

  const studyPaths = [
    {
      name: "Université",
      icon: <FaUniversity />,
      description: "Formation académique, recherche, cursus généraliste",
      degrees: ["Licence (3 ans)", "Master (2 ans)", "Doctorat (3 ans)"],
      profiles: ["Recherche", "Enseignement", "Fonction publique", "Secteurs variés"],
      color: "#1e90ff"
    },
    {
      name: "Grandes Écoles",
      icon: <FaGraduationCap />,
      description: "Formation sélective, réseau, excellence professionnelle",
      degrees: ["Prépa (2 ans)", "Concours", "Diplôme d'ingénieur/commerce"],
      profiles: ["Management", "Ingénierie", "Finance", "Conseil"],
      color: "#ff6b6b"
    },
    {
      name: "BTS / BUT",
      icon: <FaLaptopCode />,
      description: "Formation technique et professionnalisante",
      degrees: ["BTS (2 ans)", "BUT (3 ans)", "Licence Pro (1 an)"],
      profiles: ["Technicien", "Chef de projet", "Insertion rapide"],
      color: "#4ecdc4"
    },
    {
      name: "Écoles Spécialisées",
      icon: <FaHeartbeat />,
      description: "Secteurs spécifiques : art, santé, communication, etc.",
      degrees: ["Diplôme reconnu", "Formation pratique", "Stage intégré"],
      profiles: ["Créatif", "Métiers de santé", "Art", "Design"],
      color: "#ffd93d"
    }
  ];

  const careerSectors = [
    {
      name: "Tech & Digital",
      icon: <FaLaptopCode />,
      jobs: ["Développeur", "Data Scientist", "UX Designer", "Chef de projet digital"],
      demand: "Très forte",
      salary: "35-55k€",
      skills: ["Python", "JavaScript", "IA", "Agile"]
    },
    {
      name: "Marketing & Communication",
      icon: <FaChartLine />,
      jobs: ["Chef de produit", "Community Manager", "Brand Strategist", "SEO Manager"],
      demand: "Forte",
      salary: "30-45k€",
      skills: ["Créativité", "Analyse data", "Storytelling"]
    },
    {
      name: "Finance & Conseil",
      icon: <FaBriefcase />,
      jobs: ["Auditeur", "Consultant", "Analyste financier", "Contrôleur de gestion"],
      demand: "Forte",
      salary: "40-60k€",
      skills: ["Excel", "Modélisation", "Rigueur"]
    },
    {
      name: "Santé & Social",
      icon: <FaHeartbeat />,
      jobs: ["Médecin", "Infirmier", "Psychologue", "Assistant social"],
      demand: "Très forte",
      salary: "Variable",
      skills: ["Empathie", "Résilience", "Travail d'équipe"]
    },
    {
      name: "Environnement & Développement durable",
      icon: <FaGlobe />,
      jobs: ["Ingénieur environnement", "Chargé RSE", "Éco-conseiller"],
      demand: "Croissante",
      salary: "32-48k€",
      skills: ["Réglementation", "Innovation", "Impact"]
    },
    {
      name: "Éducation & Formation",
      icon: <FaBook />,
      jobs: ["Enseignant", "Formateur", "Conseiller pédagogique"],
      demand: "Stable",
      salary: "28-42k€",
      skills: ["Pédagogie", "Communication", "Adaptabilité"]
    }
  ];

  const orientationSteps = [
    {
      step: 1,
      title: "Bilan de compétences",
      description: "Identifiez vos forces, intérêts et valeurs",
      actions: [
        "Test d'orientation",
        "Analyse de vos centres d'intérêt",
        "Identification des soft skills",
        "Évaluation de vos compétences"
      ],
      icon: <FaSearch />
    },
    {
      step: 2,
      title: "Exploration des métiers",
      description: "Découvrez les secteurs et métiers qui correspondent",
      actions: [
        "Rencontres professionnelles",
        "Forums des métiers",
        "Stages d'observation",
        "Entretiens informels"
      ],
      icon: <FaUsers />
    },
    {
      step: 3,
      title: "Choix de formation",
      description: "Sélectionnez le parcours adapté",
      actions: [
        "Comparaison des formations",
        "Classement des écoles",
        "Préparation des dossiers",
        "Préparation concours"
      ],
      icon: <FaUniversity />
    },
    {
      step: 4,
      title: "Projet professionnel",
      description: "Construisez votre plan de carrière",
      actions: [
        "Définition d'objectifs",
        "Plan d'action",
        "Réseautage",
        "Veille sectorielle"
      ],
      icon: <FaRocket />
    }
  ];

  const careerServices = [
    {
      name: "Pôle Emploi",
      services: ["Conseil personnalisé", "Formations", "Offres d'emploi"],
      target: "Tous publics",
      website: "pole-emploi.fr"
    },
    {
      name: "Mission Locale",
      services: ["Accompagnement 16-25 ans", "Aide à l'insertion", "Ateliers CV"],
      target: "Jeunes 16-25 ans",
      website: "mission-locale.fr"
    },
    {
      name: "APEC",
      services: ["Cadres et jeunes diplômés", "Coaching", "Forum recrutement"],
      target: "Cadres, Bac+5",
      website: "apec.fr"
    },
    {
      name: "Bureau d'insertion universitaire",
      services: ["Stages", "Alternance", "Premier emploi", "Réseau alumni"],
      target: "Étudiants et jeunes diplômés",
      website: "Universités"
    }
  ];

  const skillCategories = [
    {
      category: "Soft skills",
      skills: [
        "Communication",
        "Travail en équipe",
        "Adaptabilité",
        "Résolution de problèmes",
        "Leadership"
      ]
    },
    {
      category: "Hard skills",
      skills: [
        "Langues étrangères",
        "Outils informatiques",
        "Compétences techniques",
        "Méthodologie",
        "Gestion de projet"
      ]
    },
    {
      category: "Savoir-être",
      skills: [
        "Proactivité",
        "Autonomie",
        "Rigueur",
        "Créativité",
        "Empathie"
      ]
    }
  ];

  const orientationTools = [
    {
      name: "Test Pédagogique",
      description: "Évaluez vos intérêts et aptitudes",
      duration: "30 min",
      icon: <FaSearch />
    },
    {
      name: "Matrice SWOT",
      description: "Analyse forces/faiblesses, opportunités/menaces",
      duration: "45 min",
      icon: <FaChartLine />
    },
    {
      name: "Carte des métiers",
      description: "Explorez les métiers par secteur",
      duration: "20 min",
      icon: <FaBriefcase />
    },
    {
      name: "Projet professionnel",
      description: "Structurez votre plan d'action",
      duration: "60 min",
      icon: <FaRegFileAlt />
    }
  ];

  const testimonials = [
    {
      name: "Sophie, 23 ans",
      path: "Licence → Master Marketing",
      quote: "L'accompagnement m'a aidée à trouver la spécialisation qui correspond à mes aspirations.",
      result: "CDI dans une agence de communication"
    },
    {
      name: "Thomas, 21 ans",
      path: "BTS → École d'ingénieurs",
      quote: "J'hésitais entre plusieurs filières. Les tests m'ont orienté vers l'informatique.",
      result: "Alternance en développement"
    },
    {
      name: "Maria, 25 ans",
      path: "Master → Doctorat",
      quote: "Le conseil m'a permis de clarifier mon projet de recherche et de trouver un laboratoire.",
      result: "Thèse CIFRE en entreprise"
    }
  ];

  return (
    <div className="co-page">
      {/* Hero Section */}
      <div className="co-hero co-hero--orientation">
        <div className="co-hero__background"></div>
        <div className="co-hero__content">
          <button className="co-back-button" onClick={() => navigate('/association')}>
            <FaArrowLeft /> Retour aux services
          </button>
          <div className="co-hero__icon">
            <FaUserGraduate />
          </div>
          <h1 className="co-hero__title">Conseil et Orientation</h1>
          <p className="co-hero__subtitle">Accompagnement personnalisé pour votre orientation académique et professionnelle</p>
        </div>
      </div>

      <div className="co-content">
        <div className="co-container">
          {/* Introduction */}
          <div className="co-intro">
            <h2 className="co-section-title">Construisez votre parcours</h2>
            <p className="co-intro__text">
              Choisir sa voie est une étape cruciale. Notre service d'orientation vous accompagne
              dans la définition de votre projet d'études et professionnel. Découvrez les formations,
              métiers et secteurs qui correspondent à vos aspirations et compétences.
            </p>
            <div className="co-stats">
              <div className="co-stat">
                <span className="co-stat__number">85%</span>
                <span className="co-stat__label">de satisfaction</span>
              </div>
              <div className="co-stat">
                <span className="co-stat__number">200+</span>
                <span className="co-stat__label">étudiants accompagnés</span>
              </div>
              <div className="co-stat">
                <span className="co-stat__number">50+</span>
                <span className="co-stat__label">filières explorées</span>
              </div>
            </div>
          </div>

          {/* Parcours d'études */}
          <div className="co-section">
            <h2 className="co-section-title">Parcours d'études en France</h2>
            <div className="co-grid co-grid--paths">
              {studyPaths.map((path, index) => (
                <div className="co-card co-card--path" key={index} style={{ borderTopColor: path.color }}>
                  <div className="co-card__icon" style={{ color: path.color }}>
                    {path.icon}
                  </div>
                  <h3 className="co-card__title">{path.name}</h3>
                  <p className="co-card__description">{path.description}</p>
                  <div className="co-card__details">
                    <div className="co-card__degrees">
                      <strong>Diplômes :</strong>
                      <ul>
                        {path.degrees.map((degree, i) => (
                          <li key={i}>{degree}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="co-card__profiles">
                      <strong>Profils :</strong>
                      <ul>
                        {path.profiles.map((profile, i) => (
                          <li key={i}>{profile}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Secteurs professionnels */}
          <div className="co-section">
            <h2 className="co-section-title">Secteurs qui recrutent</h2>
            <div className="co-grid co-grid--sectors">
              {careerSectors.map((sector, index) => (
                <div className="co-card co-card--sector" key={index}>
                  <div className="co-card__sector-icon">{sector.icon}</div>
                  <h3 className="co-card__title">{sector.name}</h3>
                  <div className="co-card__demand">
                    <span className="co-card__demand-label">Demande :</span>
                    <span className="co-card__demand-value">{sector.demand}</span>
                  </div>
                  <div className="co-card__salary">
                    <span>💰 {sector.salary}</span>
                  </div>
                  <div className="co-card__jobs">
                    <strong>Métiers :</strong>
                    <ul>
                      {sector.jobs.map((job, i) => (
                        <li key={i}>{job}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="co-card__skills">
                    <strong>Compétences clés :</strong>
                    <div className="co-skills-tags">
                      {sector.skills.map((skill, i) => (
                        <span key={i} className="co-skill-tag">{skill}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Étapes d'orientation */}
          <div className="co-section">
            <h2 className="co-section-title">Notre méthode d'accompagnement</h2>
            <div className="co-timeline">
              {orientationSteps.map((step) => (
                <div className="co-timeline__item" key={step.step}>
                  <div className="co-timeline__marker">
                    <div className="co-timeline__number">{step.step}</div>
                    <div className="co-timeline__icon">{step.icon}</div>
                  </div>
                  <div className="co-timeline__content">
                    <h3 className="co-timeline__title">{step.title}</h3>
                    <p className="co-timeline__description">{step.description}</p>
                    <div className="co-timeline__actions">
                      {step.actions.map((action, i) => (
                        <div className="co-timeline__action" key={i}>
                          <FaCheckCircle className="co-timeline__action-check" />
                          <span>{action}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Outils d'orientation */}
          <div className="co-section">
            <h2 className="co-section-title">Outils d'orientation</h2>
            <div className="co-grid co-grid--tools">
              {orientationTools.map((tool, index) => (
                <div className="co-card co-card--tool" key={index}>
                  <div className="co-card__tool-icon">{tool.icon}</div>
                  <h3 className="co-card__title">{tool.name}</h3>
                  <p className="co-card__description">{tool.description}</p>
                  <div className="co-card__duration">
                    <FaRegClock className="co-card__duration-icon" />
                    <span>{tool.duration}</span>
                  </div>
                  <button className="co-button co-button--tool">Démarrer</button>
                </div>
              ))}
            </div>
          </div>

          {/* Compétences */}
          <div className="co-section">
            <h2 className="co-section-title">Compétences à développer</h2>
            <div className="co-skills-categories">
              {skillCategories.map((category, index) => (
                <div className="co-skill-category" key={index}>
                  <h3 className="co-skill-category__title">{category.category}</h3>
                  <div className="co-skill-category__list">
                    {category.skills.map((skill, i) => (
                      <div className="co-skill-item" key={i}>
                        <FaStar className="co-skill-item__star" />
                        <span>{skill}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Services d'orientation */}
          <div className="co-section">
            <h2 className="co-section-title">Services d'accompagnement</h2>
            <div className="co-grid co-grid--services">
              {careerServices.map((service, index) => (
                <div className="co-card co-card--service" key={index}>
                  <h3 className="co-card__title">{service.name}</h3>
                  <p className="co-card__target">{service.target}</p>
                  <ul className="co-card__service-list">
                    {service.services.map((item, i) => (
                      <li key={i}>
                        <FaCheckCircle className="co-card__service-check" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <a 
                    href={`https://www.${service.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="co-card__link"
                  >
                    Visiter le site →
                  </a>
                </div>
              ))}
            </div>
          </div>

          {/* Témoignages */}
          <div className="co-section">
            <h2 className="co-section-title">Ils ont trouvé leur voie</h2>
            <div className="co-grid co-grid--testimonials">
              {testimonials.map((testimonial, index) => (
                <div className="co-card co-card--testimonial" key={index}>
                  <div className="co-card__quote">"</div>
                  <p className="co-card__testimonial-text">{testimonial.quote}</p>
                  <div className="co-card__path">{testimonial.path}</div>
                  <div className="co-card__result">
                    <FaHandshake className="co-card__result-icon" />
                    <span>{testimonial.result}</span>
                  </div>
                  <div className="co-card__name">{testimonial.name}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Section */}
          <div className="co-contact">
            <h2 className="co-section-title co-section-title--centered">Besoin d'un conseil personnalisé ?</h2>
            <p className="co-contact__text">Nos conseillers vous accompagnent dans la construction de votre projet</p>
            <div className="co-contact-buttons">
              <Link to="/contact" className="co-button co-button--primary">
                <FaUserGraduate /> Prendre rendez-vous
              </Link>
              <Link to="/rendez-vous" className="co-button co-button--secondary">
                <FaSearch /> Bilan d'orientation
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConseilOrientation;